import { isPromise } from "radash";

import { isError } from "./errorHandling.ts";

function memoize<ArgsType extends unknown[], Result>(
  callback: (...args: ArgsType) => Result,
  {
    key,
    staleTime,
  }: { key?: (...args: ArgsType) => unknown; staleTime?: number } = {},
): (...args: ArgsType) => Result {
  const cache = new Map<unknown, Result>();

  return (...args: ArgsType) => {
    const cacheKey = key?.(...args) ?? JSON.stringify(args);
    const cachedResult = cache.get(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    const result = callback(...args);
    cache.set(cacheKey, result);

    if (staleTime) {
      setTimeout(() => cache.delete(cacheKey), staleTime);
    }

    if (isPromise(result)) {
      result
        .then((value) => {
          if (isError(value)) {
            cache.delete(cacheKey);
          }
        })
        .catch((error: unknown) => {
          cache.delete(cacheKey);
          throw error;
        });
    }

    return result;
  };
}

function singleton<Result>(callback: () => Result): {
  (): Result;
  reset: () => void;
} {
  let instance: Result | null = null;

  const wrapped = () => {
    if (instance) {
      return instance;
    }

    const result = callback();
    instance = result;

    if (result instanceof Promise) {
      result
        .then((value) => {
          if (isError(value)) {
            instance = null;
          }
        })
        .catch((error: unknown) => {
          instance = null;
          throw error;
        });
    }

    return instance;
  };

  wrapped.reset = () => {
    instance = null;
  };

  return wrapped;
}

export { memoize, singleton };
